import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { OfferListSortOption } from 'constants/offerListFilters'

function useOfferListSortOptions() {
  const intl = useIntl()

  return useMemo(() => {
    const SORT_OPTION_NEW_HOME_PAGE: OfferListSortOption = {
      label: intl.formatMessage({
        id: '3_Isaq9FFvvrRAEzpan7',
        defaultMessage: 'Recommended',
        description: 'Sort option label for recommended offers on new home page',
      }),
      value: 'home',
    }

    const SORT_OPTION_HOME_PAGE: OfferListSortOption = {
      label: intl.formatMessage({
        id: 'F5oc3HIckKLFU0G_187-',
        defaultMessage: 'Recommended',
        description: 'Sort option label for recommended offers on home page',
      }),
      value: 'homepage',
    }

    const SORT_OPTION_RECOMMENDED: OfferListSortOption = {
      label: intl.formatMessage({
        id: 'xCfG-BqN_4hiZ-8TVmBx',
        defaultMessage: 'Recommended',
        description: 'Sort option label for recommended offers on the offer page',
      }),
      value: 'recommended',
    }

    const SORT_OPTION_NEWEST: OfferListSortOption = {
      label: intl.formatMessage({
        id: 'YbmMOMVsaWyKHKg-aAdz',
        defaultMessage: 'Latest',
        description: 'Sort option label for latest offers on the offer listing or detail page',
      }),
      value: 'newest',
    }

    const SORT_OPTION_POPULARITY: OfferListSortOption = {
      label: intl.formatMessage({
        id: '9eiuk1fSEr9owAEn4jrK',
        defaultMessage: 'Bestseller',
        description: 'Sort option label for bestselling offers on the offer page',
      }),
      value: 'popularity',
    }

    const SORT_OPTION_PRICE_ASC: OfferListSortOption = {
      label: intl.formatMessage({
        id: 'AUQEXnejPNxbArYkEu7x',
        defaultMessage: 'Price (Lowest)',
        description: 'Sort option label for sorting by lowest price in the offer listing section',
      }),
      value: 'price.asc',
    }

    const SORT_OPTION_PRICE_DESC: OfferListSortOption = {
      label: intl.formatMessage({
        id: 'bhLmAN0t8vWbystHPWW-',
        defaultMessage: 'Price (Highest)',
        description: 'Sort option label for sorting by highest price in the offer listing section',
      }),
      value: 'price.desc',
    }

    const SORT_OPTION_DISTANCE: OfferListSortOption = {
      label: intl.formatMessage({
        id: 'EntkSVgn9UAa4eYPPOD-',
        defaultMessage: 'Distance',
        description: 'Sort option label for sorting by distance in the offer listing section',
      }),
      value: 'distance',
    }

    const SORT_OPTION_VALUE: OfferListSortOption = {
      label: intl.formatMessage({
        id: 'OlJljjzBcXLOB-hI1T8z',
        defaultMessage: 'Best Value',
        description: 'Sort option label for sorting by best value in the offer listing section',
      }),
      value: 'value',
    }

    const SORT_OPTION_CUSTOMER_RATING: OfferListSortOption = {
      label: intl.formatMessage({
        id: 'X3dQ1SCiQe_urahMsHNL',
        defaultMessage: 'Customer Rating (Highest)',
        description: 'Sort option label for sorting by highest customer rating',
      }),
      value: 'customerRating',
    }

    const SORT_OPTION_INCLUSIONS: OfferListSortOption = {
      label: intl.formatMessage({
        id: 'XcMdoZIpYlmOk-uRp56g',
        defaultMessage: 'Most Inclusions',
        description: 'Sort option label for sorting by most inclusions in the offer listing section',
      }),
      value: 'inclusions',
    }

    const SORT_OPTION_SALES: OfferListSortOption = {
      label: intl.formatMessage({
        id: '9ixqqlyLYcmgKZVxiLd8',
        defaultMessage: 'Trending',
        description: 'Sort option label for sorting by trending sales in the offer listing section',
      }),
      value: 'sales',
    }

    const SORT_OPTION_ENDING_SOON: OfferListSortOption = {
      label: intl.formatMessage({
        id: 'PlYEpoPvAahZK6IXuYGv',
        defaultMessage: 'Ending Soon',
        description: 'Sort option label for offers ending soon on the offer page',
      }),
      value: 'endingsoon',
    }

    const SORT_DEPARTURE_SOON: OfferListSortOption = {
      label: intl.formatMessage({
        id: 'NFy0PXED6cIt_jujXy9O',
        defaultMessage: 'Depart soon',
        description: 'Sort option label for sorting by soonest departure date',
      }),
      value: 'departure_date.asc',
    }

    const SORT_NEWEST: OfferListSortOption = {
      label: intl.formatMessage({
        id: 'D625F2t-HN_77y3ueaAq',
        defaultMessage: 'Newest',
        description: 'Sort option label for sorting by most recently updated',
      }),
      value: 'updated_at.desc',
    }

    const TOURS_PRICE_LOWEST: OfferListSortOption = {
      label: intl.formatMessage({
        id: 'lgA-JhTNFtmDeRTvpTC4',
        defaultMessage: 'Price (Lowest)',
        description: 'Sort option label for sorting tours by lowest price',
      }),
      value: 'price.asc',
    }

    const TOURS_PRICE_HIGHEST: OfferListSortOption = {
      label: intl.formatMessage({
        id: 'HJRcbShKC77nZlavzjMC',
        defaultMessage: 'Price (Highest)',
        description: 'Sort option label for sorting tours by highest price',
      }),
      value: 'price.desc',
    }

    const TOURS_NUMBER_OF_PLACES_LOWEST: OfferListSortOption = {
      label: intl.formatMessage({
        id: 'IsYCihl_FVBmGgEM1aR-',
        defaultMessage: 'Number of places visited (Lowest)',
        description: 'Sort option label for sorting tours by fewest places visited',
      }),
      value: 'numberOfPlacesVisited.asc',
    }

    const TOURS_NUMBER_OF_PLACES_HIGHEST: OfferListSortOption = {
      label: intl.formatMessage({
        id: 'Rc442iu5q6B4rlz1g1Bu',
        defaultMessage: 'Number of places visited (Highest)',
        description: 'Sort option label for sorting tours by most places visited',
      }),
      value: 'numberOfPlacesVisited.desc',
    }

    const TOURS_DURATION_SHORTEST: OfferListSortOption = {
      label: intl.formatMessage({
        id: '6_p5aPeRHE5yDn7sUV0L',
        defaultMessage: 'Duration (Shortest)',
        description: 'Sort option label for sorting tours by shortest duration',
      }),
      value: 'tourLength.asc',
    }

    const TOURS_DURATION_LONGEST: OfferListSortOption = {
      label: intl.formatMessage({
        id: 'O_a8hjE8sTHNAa2n-RVk',
        defaultMessage: 'Duration (Longest)',
        description: 'Sort option label for sorting tours by longest duration',
      }),
      value: 'tourLength.desc',
    }

    const frontPageSortByTypes: Array<OfferListSortOption> = [
      SORT_OPTION_RECOMMENDED,
      SORT_OPTION_VALUE,
      SORT_OPTION_PRICE_ASC,
      SORT_OPTION_PRICE_DESC,
      SORT_OPTION_CUSTOMER_RATING,
    ]

    const searchSortByTypes: Array<OfferListSortOption> = [
      SORT_OPTION_RECOMMENDED,
      SORT_OPTION_PRICE_ASC,
      SORT_OPTION_PRICE_DESC,
      SORT_OPTION_VALUE,
    ]

    const landmarkSearchSortByTypes: Array<OfferListSortOption> = [
      SORT_OPTION_RECOMMENDED,
      SORT_OPTION_PRICE_ASC,
      SORT_OPTION_PRICE_DESC,
      SORT_OPTION_DISTANCE,
    ]

    const tourSortByTypes: Array<OfferListSortOption> = [
      SORT_OPTION_RECOMMENDED,
      TOURS_PRICE_LOWEST,
      TOURS_PRICE_HIGHEST,
      SORT_OPTION_VALUE,
      TOURS_NUMBER_OF_PLACES_LOWEST,
      TOURS_NUMBER_OF_PLACES_HIGHEST,
      TOURS_DURATION_SHORTEST,
      TOURS_DURATION_LONGEST,
    ]

    const homesAndVillasSortByTypes: Array<OfferListSortOption> = [
      SORT_OPTION_RECOMMENDED,
      SORT_OPTION_PRICE_ASC,
      SORT_OPTION_PRICE_DESC,
      SORT_OPTION_VALUE,
    ]

    return {
      SORT_OPTION_NEW_HOME_PAGE,
      SORT_OPTION_HOME_PAGE,
      SORT_OPTION_RECOMMENDED,
      SORT_OPTION_NEWEST,
      SORT_OPTION_POPULARITY,
      SORT_OPTION_PRICE_ASC,
      SORT_OPTION_PRICE_DESC,
      SORT_OPTION_DISTANCE,
      SORT_OPTION_VALUE,
      SORT_OPTION_CUSTOMER_RATING,
      SORT_OPTION_INCLUSIONS,
      SORT_OPTION_SALES,
      SORT_OPTION_ENDING_SOON,
      SORT_DEPARTURE_SOON,
      SORT_NEWEST,
      TOURS_PRICE_LOWEST,
      TOURS_PRICE_HIGHEST,
      TOURS_NUMBER_OF_PLACES_LOWEST,
      TOURS_NUMBER_OF_PLACES_HIGHEST,
      TOURS_DURATION_SHORTEST,
      TOURS_DURATION_LONGEST,
      frontPageSortByTypes,
      searchSortByTypes,
      landmarkSearchSortByTypes,
      tourSortByTypes,
      homesAndVillasSortByTypes,
    }
  }, [intl])
}

export default useOfferListSortOptions
